import { createTheme } from "@material-ui/core/styles";
import { teal, pink } from "@material-ui/core/colors";

/**
 * @constant SCROLLBAR_THUMB
 * @description Colour of the scrollbar thumb. A neutral translucent grey so the same value reads
 * correctly over both the light and the dark module themes without needing two palettes.
 */
export const SCROLLBAR_THUMB = 'rgba(128, 128, 128, 0.45)';

/**
 * @constant OVERLAY_COLUMN_BOTTOM_SPACE
 * @description Breathing room, in px, left under the last card of an overlay column, so a column
 * scrolled to its end stops with a gap instead of the card sitting flush against the bottom edge.
 * Applied as a margin on the last child rather than as the column's own `padding-bottom`, since a
 * flex scroll container's bottom padding is not reliably part of its scrollable area.
 */
export const OVERLAY_COLUMN_BOTTOM_SPACE = 10;

const theme = createTheme({
  palette: {
    primary: teal,
    secondary: pink,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${SCROLLBAR_THUMB} transparent`,
        },
        '*::-webkit-scrollbar': {
          width: 6,
          height: 6,
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: SCROLLBAR_THUMB,
          borderRadius: 3,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(128, 128, 128, 0.7)',
        },
        '*::-webkit-scrollbar-button': {
          display: 'none',
          width: 0,
          height: 0,
        },
        '*::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
        '[data-panels-collapsed]': {
          scrollbarColor: 'transparent transparent',
          transition: 'scrollbar-color 0.2s ease',
        },
        '[data-panels-collapsed]:hover': {
          scrollbarColor: `${SCROLLBAR_THUMB} transparent`,
        },
        '[data-panels-collapsed]::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          transition: 'background-color 0.2s ease',
        },
        '[data-panels-collapsed]:hover::-webkit-scrollbar-thumb': {
          backgroundColor: SCROLLBAR_THUMB,
        },
        '[data-panels-collapsed="true"] > *:not([data-panels-toggle]):not([data-panels-collapsed])': {
          display: 'none !important',
        },
        /**
         * Keeps every overlay column scrollable instead of squashing its panels. Each column is a
         * column-direction flex container, and a flex item defaults to `flex-shrink: 1`: once the
         * stack is taller than the column - which is what happens on a shorter screen - the browser
         * compresses the panels to fit rather than letting them overflow. The column then measures
         * exactly full, so `overflow-y: auto` sees nothing to scroll and shows no scrollbar, while
         * the panels themselves are left cut. Refusing the shrink makes the stack overflow honestly
         * and the column's own scrollbar appear.
         * Scoped to `[data-panels-collapsed]`, which `OverlayPanelsToggle` sets on the column it
         * sits in (always, "true" or "false"), so it reaches every column of every module from one
         * place rather than through a `flexShrink` added by hand to each card on each result page.
         */
        '[data-panels-collapsed] > *': {
          flexShrink: 0,
        },
        /**
         * Reaches every module's columns from the same marker as the rule above.
         * @see {@link OVERLAY_COLUMN_BOTTOM_SPACE}
         */
        '[data-panels-collapsed] > *:last-child': {
          marginBottom: OVERLAY_COLUMN_BOTTOM_SPACE,
        },
      },
    },
  },
});

// @TODO remove
export const smallIconButton = (outerTheme) => ({
  ...outerTheme,
  overrides: {
    MuiIconButton: {
      root: {
        width: outerTheme.spacing(4),
        height: outerTheme.spacing(4),
        fontSize: outerTheme.typography.pxToRem(20),
      },
    },
  },
});

// @TODO remove
export const mediumIconButton = (outerTheme) => ({
  ...outerTheme,
  overrides: {
    ...outerTheme.overrides,
    MuiIconButton: {
      root: {
        width: outerTheme.spacing(5),
        height: outerTheme.spacing(5),
      },
    },
  },
});

// @TODO remove
export const lessPaddedExpansion = (outerTheme) => ({
  ...outerTheme,
  overrides: {
    ...outerTheme.overrides,
    MuiExpansionPanelSummary: {
      root: {
        padding: outerTheme.spacing(0, 2),
      },
    },
    MuiExpansionPanelDetails: {
      root: {
        padding: outerTheme.spacing(2, 2),
      },
    },
  },
});

export default theme;

/**
 * @constant ACQUISITION_CARD_WIDTH
 * @description How wide the card holding a module's acquisition steps is.
 * It was written three different ways across the eight modules - `width: 80vw` with a 1000px
 * cap on five of them, `minWidth: 80vw` with the same cap on two, and `minWidth: 60vw` with no
 * real cap on EUSCoast. The two spellings do not mean the same thing: a minimum width beats a
 * maximum in CSS, so the pair that used `minWidth` kept growing past 1000px while the others
 * stopped there, and the same step came out visibly narrower from one module to the next.
 * One share of the window, the same everywhere, which is the wider behaviour the modules that
 * used `minWidth` already had.
 * @memberof module:THEME
 */
export const ACQUISITION_CARD_WIDTH = '80vw';

/**
 * @constant STEPPER_MAX_WIDTH
 * @description How wide the numbered step bar at the top of an acquisition card gets, in px.
 * Capped rather than left to fill the card: at full width the connectors between the steps
 * stretch across the screen and the bar stops reading as a sequence.
 * It was 800 on two modules, 700 on three and 620 on three more - the same bar, in three sizes,
 * for no reason anyone could point at. The widest is the one that holds the longest labels of a
 * five-step flow without wrapping, so that is the one that stays.
 * @memberof module:THEME
 */
export const STEPPER_MAX_WIDTH = 800;

/**
 * @constant CHOICE_GRID_MAX_WIDTH
 * @description How wide a row of choice cards gets, in px - the two or three cards a step puts
 * side by side for the user to pick from.
 * Capped and centred rather than filling the card: the card is a share of the window, so on a
 * wide screen a pair of cards laid across it ends up with the two choices at opposite ends of the
 * screen, reading as two unrelated things rather than as a choice between them.
 * @memberof module:THEME
 */
export const CHOICE_GRID_MAX_WIDTH = 960;
